"use client";
import React, { useState } from "react";
import Modal, { ModalProps, ModalTitle } from "@/app/pwa/components/Modal";
import { Person } from "@/sources/kicker-amateur/types";
import { UserInfo } from "@/sources/kicker-ovsyndication";
import Button from "@/app/pwa/components/Button";
import { getPersonFullName } from "@/helpers/personHelpers";
import { TextAreaField } from "@/app/pwa/components/form/Textarea";
import { InputField } from "@/app/pwa/components/form/InputField";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { userInquiryCreateRequestSchema } from "@vereinsheim/backend-types/src/userInquiry";
import { z } from "zod";
import IconSend from "@/assets/icon/send.svg";
import { set } from "@/utils/storage";

const formSchema = userInquiryCreateRequestSchema.omit({ personId: true });
type UserInquiryFormData = z.infer<typeof formSchema>;

type UserInquiryModalProps = {
  person: Person;
  user: UserInfo;
  onSubmit: (firstName: string, lastName: string, message: string) => Promise<void>;
} & ModalProps;

export function UserInquiryModal({
  person,
  user,
  onSubmit,
  children,
  ...rest
}: UserInquiryModalProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<UserInquiryFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      message: "",
    }
  });

  const [isSending, setIsSending] = useState(false);

  const onSubmitForm = async (data: UserInquiryFormData) => {
    try {
      setIsSending(true);
      await onSubmit(data.firstName.trim(), data.lastName.trim(), data.message.trim());
      rest.onRequestClose?.();
    } catch (err) {
      setIsSending(false);
      setError("message", {
        type: "server",
        message: "Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut."
      });
    }
  };

  return (
    <Modal {...rest} title={`Anfrage für ${getPersonFullName(person)}`}>
      <form onSubmit={handleSubmit(onSubmitForm)} className="flex flex-col space-y-4 relative max-w-lg">
        <ModalTitle className="px-6 w-full">
          Bist du {getPersonFullName(person)}?
        </ModalTitle>

        <div className="px-6">
          <p className="">
            Bitte gib deine Kontaktdaten an, damit wir deine Anfrage überprüfen können.
            Dabei hilft uns z.B. auch die Angabe deiner Telefonnummer oder deines Geburtsdatums.
          </p>
        </div>
        
        <div className="px-6 space-y-4">
          <div className="flex gap-4">
            <InputField
              id="firstName"
              label="Vorname *"
              register={register("firstName")}
              error={errors.firstName}
              fieldsetClassName="w-1/2"
            />

            <InputField
              id="lastName"
              label="Nachname *"
              register={register("lastName")}
              error={errors.lastName}
              fieldsetClassName="w-1/2"
            />
          </div>

          <TextAreaField
            id="message"
            label="Kontaktdaten & Nachricht *"
            placeholder="Adresse, Telefonnummer, Geburtsdatum, etc."
            register={register("message")}
            error={errors.message}
            rows={4}
          />
        </div>

        <div className="px-6 py-6 pt-2">
          <Button
            type="submit"
            color="primary"
            className="w-full justify-center"
            loading={isSending}
          >
            Anfrage senden
            <IconSend className="button__icon" />
          </Button>
        </div>
      </form>
    </Modal>
  );
} 
